import React, { useEffect, useRef, useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import "./NewCardModal.css";

const NewCardModal = ({
  handleClose,
  showModal,
  toggleAlert,
  toggleRefresh,
  clientSecret,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [paymentElementMounted, setPaymentElementMounted] = useState(false);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Esc to close + body scroll lock while open
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !isLoading) handleClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the close button for keyboard users
    setTimeout(() => closeBtnRef.current?.focus(), 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showModal, isLoading, handleClose]);

  // Reset transient state when modal opens
  useEffect(() => {
    if (showModal) {
      setError(null);
    }
  }, [showModal]);

  const handleSaveCard = async () => {
    setIsLoading(true);
    setError(null);

    if (
      !stripe ||
      !elements ||
      !clientSecret ||
      !isMounted ||
      !paymentElementMounted
    ) {
      setIsLoading(false);
      return;
    }

    try {
      await elements.submit();
      const result = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/completion`,
        },
        redirect: "if_required",
      });

      if (result.error) {
        setError(result.error.message || "We couldn't save this card.");
        console.error(result.error);
      } else {
        handleClose();
        toggleRefresh();
        toggleAlert();
      }
    } catch (err) {
      console.error("Error saving card:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!showModal) return null;

  const canSubmit =
    stripe &&
    elements &&
    !isLoading &&
    clientSecret &&
    paymentElementMounted;

  return (
    <div
      className="nc-modal-overlay"
      onClick={() => !isLoading && handleClose()}
      role="presentation"
    >
      <div
        className="nc-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nc-modal-title"
        aria-describedby="nc-modal-subtitle"
      >
        {/* Header */}
        <div className="nc-modal-header">
          <div className="nc-header-row">
            <div className="nc-header-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="3" />
                <path d="M2 10h20" />
                <path d="M6 15h4" />
              </svg>
            </div>
            <div>
              <h2 id="nc-modal-title" className="nc-modal-title">Add a payment card</h2>
              <p id="nc-modal-subtitle" className="nc-modal-subtitle">
                Your card details are encrypted and stored by Stripe — never on our servers.
              </p>
            </div>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="nc-close-button"
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Close add card dialog"
            title="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Card preview */}
        <div className="nc-preview-wrap">
          <div className="nc-preview-card" aria-hidden="true">
            <div className="nc-preview-shimmer" />
            <div className="nc-preview-top">
              <div className="nc-preview-chip" />
              <div className="nc-preview-brand">SECURE</div>
            </div>
            <div className="nc-preview-number">
              <span>••••</span>
              <span>••••</span>
              <span>••••</span>
              <span className="nc-preview-number-last">••••</span>
            </div>
            <div className="nc-preview-bottom">
              <div>
                <div className="nc-preview-label">CARDHOLDER</div>
                <div className="nc-preview-value">YOUR NAME</div>
              </div>
              <div>
                <div className="nc-preview-label">EXPIRES</div>
                <div className="nc-preview-value">MM / YY</div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="nc-modal-body">
          <div className="nc-trust-row" role="status">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>256-bit TLS · PCI DSS Level 1 · Powered by Stripe</span>
            <div className="nc-brands" aria-hidden="true">
              <span className="nc-brand-chip">VISA</span>
              <span className="nc-brand-chip">MC</span>
              <span className="nc-brand-chip">AMEX</span>
            </div>
          </div>

          <div className="nc-payment-element">
            {!paymentElementMounted && <div className="nc-element-skeleton" />}
            <PaymentElement
              onReady={() => setPaymentElementMounted(true)}
              options={{ layout: "tabs" }}
            />
          </div>

          {error && (
            <div className="nc-error-alert" role="alert" aria-live="polite">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="nc-modal-footer">
          <button
            type="button"
            className="nc-button nc-button-ghost"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="nc-button nc-button-primary"
            onClick={handleSaveCard}
            disabled={!canSubmit}
            aria-busy={isLoading || undefined}
          >
            {isLoading ? (
              <>
                <span className="nc-spinner" aria-hidden="true" />
                <span>Saving…</span>
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12l5 5L20 7" />
                </svg>
                <span>Save card securely</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewCardModal;
